import React, { useCallback, useMemo } from 'react'
import { Checkbox, Tooltip } from 'antd'
import { includes, isNil, some } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import {
  useLoggedInUser,
  useUserHasPermissions,
  useUserHasRoles,
} from '../../../account/hooks'
import {
  checkIfQaAssignableUser,
  checkIfQcAssignableUser,
} from '../../../account/utils'
import {
  PERMISSIONS,
  ROLES,
  USER_ASSIGNMENT_ACTION_TYPE,
} from '../../constants'
import { StyledCheckboxSpinner } from '../styled'

const UserAssignmentCheckbox = ({
  assignedUser,
  tooltip,
  onChange,
  otherAssignedUsers,
  userActionType,
  unassignedLabel,
  isLoading,
  isDisabled,
}) => {
  const user = useLoggedInUser()

  const isUserQaTrainee = useUserHasRoles([ROLES.QA_TRAINEE.value])
  const isUserQcTrainee = useUserHasRoles([ROLES.QC_TRAINEE.value])

  const hasPermissionToAssignCurrentUser = useUserHasPermissions(
    PERMISSIONS.MENU_CURATION_TASK.ASSIGN_USER,
  )

  const isChecked = useMemo(
    () => !isNil(assignedUser?.name),
    [assignedUser?.name],
  )

  const isUnassignAllowed = useMemo(
    () => areSameValue(user?.id, assignedUser?.id),
    [assignedUser?.id, user?.id],
  )

  const isAssignationDisabled = useMemo(() => {
    const isSameUser = some(otherAssignedUsers, userId =>
      areSameValue(userId, user?.id),
    )

    if (isChecked && isUnassignAllowed) {
      return false
    }

    const isQaAction = includes(
      [
        USER_ASSIGNMENT_ACTION_TYPE.QA,
        USER_ASSIGNMENT_ACTION_TYPE.UNKNOWN_WORDS_QA,
      ],
      userActionType,
    )
    const isQaAssignable = checkIfQaAssignableUser(user)

    const isQcAction = userActionType === USER_ASSIGNMENT_ACTION_TYPE.QC
    const isQcAssignable = checkIfQcAssignableUser(user)

    return (
      isDisabled ||
      !hasPermissionToAssignCurrentUser ||
      (isChecked && !isUnassignAllowed) ||
      isSameUser ||
      (isQaAction && isUserQaTrainee) ||
      (isQaAction && !isQaAssignable) ||
      (isQcAction && !isQcAssignable) ||
      (isQcAction && isUserQcTrainee)
    )
  }, [
    hasPermissionToAssignCurrentUser,
    isChecked,
    isDisabled,
    isUnassignAllowed,
    isUserQaTrainee,
    isUserQcTrainee,
    otherAssignedUsers,
    user,
    userActionType,
  ])

  const userAssignmentLabel = useMemo(
    () => (assignedUser ? assignedUser.name : unassignedLabel),
    [assignedUser, unassignedLabel],
  )

  const handleOnChange = useCallback(() => {
    onChange(!isChecked ? user.id : null)
  }, [isChecked, onChange, user.id])

  return isLoading ? (
    <div>
      <StyledCheckboxSpinner />
      {userAssignmentLabel}
    </div>
  ) : (
    <Tooltip title={tooltip}>
      <Checkbox
        checked={isChecked}
        disabled={isAssignationDisabled}
        onChange={handleOnChange}
      >
        {userAssignmentLabel}
      </Checkbox>
    </Tooltip>
  )
}

UserAssignmentCheckbox.propTypes = {
  onChange: PropTypes.func.isRequired,
  assignedUser: PropTypes.object,
  isLoading: PropTypes.bool,
  tooltip: PropTypes.string,
  otherAssignedUsers: PropTypes.arrayOf(PropTypes.string),
  userActionType: PropTypes.string,
  isDisabled: PropTypes.bool,
  unassignedLabel: PropTypes.string,
}

UserAssignmentCheckbox.defaultProps = {
  assignedUser: undefined,
  isLoading: false,
  tooltip: undefined,
  userActionType: undefined,
  otherAssignedUsers: [],
  isDisabled: false,
  unassignedLabel: 'Assign to me',
}

export default UserAssignmentCheckbox
